<template>
  <div class="relative" ref="rootEl">
    <button
      type="button"
      @click="toggle"
      @keydown.esc="close"
      :aria-expanded="open"
      aria-haspopup="listbox"
      class="h-7 inline-flex items-center gap-1.5 pl-2.5 pr-2 text-xs font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      :class="isDefault
        ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
        : 'bg-blue-600 text-white hover:bg-blue-700'"
    >
      <!-- The label stays visible even when a value is picked. On a bar of six
           controls, "Recurring" alone does not tell you which filter it is. -->
      <span :class="isDefault ? 'text-gray-400 dark:text-gray-500' : 'text-white/70'">{{ label }}</span>
      <span>{{ selected.label }}</span>
      <svg
        class="w-3 h-3 flex-shrink-0 transition-transform duration-150"
        :class="[open ? 'rotate-180' : '', isDefault ? 'text-gray-400 dark:text-gray-500' : 'text-white/70']"
        fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <div v-if="open" @click="close" class="fixed inset-0 z-20"></div>

    <Transition name="select-pop">
      <div
        v-if="open"
        role="listbox"
        :aria-label="label"
        class="absolute left-0 top-full mt-1.5 z-30 w-72 max-w-[calc(100vw-2rem)] p-1.5 bg-white dark:bg-[#2A3037] border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg ring-1 ring-black/5"
      >
        <button
          v-for="opt in options"
          :key="opt.value"
          type="button"
          role="option"
          :aria-selected="opt.value === modelValue"
          @click="choose(opt)"
          class="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-800"
          :class="opt.value === modelValue
            ? 'bg-blue-50/70 dark:bg-blue-900/20'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800'"
        >
          <!-- Fixed-width gutter rather than a checkmark pushed to the right.
               With two lines of text per row, a trailing tick drifts far from
               the label it belongs to and stops reading as a selection. -->
          <span class="w-4 flex-shrink-0 flex items-center justify-center pt-0.5">
            <svg
              v-if="opt.value === modelValue"
              class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400"
              fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
          </span>

          <span class="min-w-0">
            <span
              class="block text-[13px] font-semibold leading-snug"
              :class="opt.value === modelValue
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-gray-800 dark:text-gray-100'"
            >{{ opt.label }}</span>
            <span class="block text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400 mt-0.5">
              {{ opt.description }}
            </span>
          </span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
// A single-select dropdown where each option carries a description as well as
// a name. A native <select> cannot do this at all, and TaskSelectMenu is built
// for single-line pills: its rows are `items-center` and its panel is narrow,
// both of which fight a two-line option.
//
// Worth the separate component because these filters are not self-explanatory.
// "Recurring" in particular does not mean what most people assume, and a
// dropdown is the only place that misunderstanding can be corrected at the
// moment it matters.
import { ref, computed, onBeforeUnmount } from 'vue'

const props = defineProps({
  label: { type: String, required: true },
  modelValue: { type: String, default: '' },
  // [{ value, label, description }]
  options: { type: Array, required: true },
  // Which value counts as "no filter applied", so the trigger can render
  // quietly rather than shouting that a filter is active when none is.
  defaultValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const rootEl = ref(null)

const selected = computed(() =>
  props.options.find(o => o.value === props.modelValue) || props.options[0] || { label: '' })

const isDefault = computed(() => props.modelValue === props.defaultValue)

function toggle() {
  open.value ? close() : openMenu()
}

function openMenu() {
  open.value = true
  document.addEventListener('keydown', onKeydown)
}

function close() {
  open.value = false
  document.removeEventListener('keydown', onKeydown)
}

// Escape closes from anywhere, not just while the trigger holds focus — once
// the panel is open the pointer is usually over the list, not the button.
function onKeydown(event) {
  if (event.key === 'Escape') close()
}

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))

function choose(option) {
  emit('update:modelValue', option.value)
  close()
}
</script>

<style scoped>
.select-pop-enter-active {
  transition: opacity 0.12s ease, transform 0.14s cubic-bezier(0.22, 1, 0.36, 1);
}
.select-pop-leave-active {
  transition: opacity 0.1s ease;
}
.select-pop-enter-from {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
.select-pop-leave-to {
  opacity: 0;
}
</style>
